import React, { createContext, useContext, useState, useEffect } from "react";
import Swal from 'sweetalert2';
import '../Assets/scss/swit-alert2.css';

// Cookies
import Cookies from "universal-cookie";
import socket from "../socket"
import { getProfile } from "../api/user";
// Services
import Notification from "./../Services/Notification/Notification";
import { getCount } from "./../api/notification";

export const AppContext = createContext();

function AppProvider({ children }) {
  const [notificationCount, setNotificationCount] = useState(0);
  const [isLogin, setIsLogin] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [alert, setAlert] = useState(null);
  const [role, setRole] = useState(null);
  const [timezone, setTimezone] = useState(null);
  const [parentStoreTimezone, setParentStoreTimezone] = useState(null);
  const [lastOrderEditPath, setLastOrderEditPath] =
    useState("/admin/orders/list");

  const cookies = new Cookies();
  const [modules, setModules] = useState(
    // convertPermissionsIntToBoolean(cookies.get("modules") || [])
    convertPermissionsIntToBoolean(
      JSON.parse(localStorage.getItem("modules")) || []
    )
  );
  const notificationService = new Notification();

  const login = async (token, role, timezone, modules, parentStoreTimezone) => {
    try {
      setToken(token);
      setRole(role);
      setTimezone(timezone);
      setParentStoreTimezone(parentStoreTimezone);
      setIsLogin(true);
      setModules(convertPermissionsIntToBoolean(modules));
      cookies.set("defaultTimezone", timezone, { path: "/" });
      cookies.set("accessToken", token, { path: "/" });
      cookies.set("role", role, { path: "/" });
      cookies.set("parentStoreTimezone", parentStoreTimezone, { path: "/" });
      localStorage.setItem("modules", JSON.stringify(modules));
      
      let response = await getProfile();
      if (response && response.status === 200 && response.data?.data?.id) {
        try {
          socket.connect();
          socket.emit("join", response.data.data.id);
        } catch (socketError) {
          console.error("Socket connection error:", socketError);
        }
      }
    } catch (error) {
      console.error("Error during login:", error);
      // Handle login errors gracefully
    }
  };

  const logout = () => {
    setToken(null);
    setRole(null);
    setTimezone(null);
    setParentStoreTimezone(null);
    setIsLogin(false);
    setModules([]);

    cookies.remove("role", { path: "/" });
    cookies.remove("defaultTimezone", { path: "/" });
    cookies.remove("parentStoreTimezone", { path: "/" });
    cookies.remove("accessToken", { path: "/" });
    // cookies.remove("modules", { path: "/" });
    localStorage.removeItem("modules");
    localStorage.removeItem("assignStore");
    localStorage.removeItem("selectedStore");
    localStorage.removeItem("storeTimezone");

    if (window.zE) {
      try {
        window.zE("webWidget", "hide");
        window.zE("webWidget:on", "close");
      } catch (zendeskError) {
        console.error("Zendesk error:", zendeskError);
      }
    }

    try {
      socket.disconnect();
    } catch (socketError) {
      console.error("Socket disconnect error:", socketError);
    }
  };

  const changeModules = (modules) => {
    setModules(convertPermissionsIntToBoolean(modules));
    // cookies.set("modules", modules, { path: "/" });
    localStorage.setItem("modules", JSON.stringify(modules));
  };

  useEffect(() => {
    try {
      let accessToken = cookies.get("accessToken");
      let timezone = cookies.get("defaultTimezone");
      let parentStoreTimezone = cookies.get("parentStoreTimezone");
      let role = cookies.get("role");
      let modules = null;
      
      try {
        modules = JSON.parse(localStorage.getItem("modules"));
      } catch (parseError) {
        console.error("Error parsing modules from localStorage:", parseError);
        modules = null;
      }

      if (accessToken) {
        setToken(accessToken);
        setIsLogin(true);
        handleGetNotificationCount();
      }

      if (role) setRole(role);
      if (timezone) setTimezone(timezone);
      if (parentStoreTimezone) setParentStoreTimezone(parentStoreTimezone);
      if (modules) setModules(convertPermissionsIntToBoolean(modules));
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) setToken(token);
  }, [token]);

  useEffect(() => {
    if (role) setRole(role);
  }, [role]);

  useEffect(() => {
    if (timezone) setTimezone(timezone);
  }, [timezone]);

  useEffect(() => {
    if (parentStoreTimezone) setParentStoreTimezone(parentStoreTimezone);
  }, [parentStoreTimezone]);

  useEffect(() => {
    if (modules) setModules(modules);
  }, [modules]);

  useEffect(() => {
    async function fetchProfile() {
      try {
        if (isLogin) {
          let response = await getProfile();
          if (response && response.status === 200 && response.data?.data?.id) {
            try {
              socket.connect();
              socket.emit("join", response.data.data.id);
            } catch (socketError) {
              console.error("Socket connection error:", socketError);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
        // Handle error gracefully
      }
    }
    
    if (isLogin) {
      fetchProfile();
    }
  }, [isLogin]);

  const showToastMessage = (title, message, confirmButtonStyle = "success", showCancel = false, timeOut = 8000) => {
    // Hide any existing alerts first
    Swal.close();
    
    const swalConfig = {
      title: title || "",
      text: message,
      showCancelButton: showCancel,
      confirmButtonText: 'OK',
      cancelButtonText: 'Cancel',
      timer: timeOut,
      timerProgressBar: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
      customClass: {
        popup: confirmButtonStyle === "info" ? "swt-alert-info" : "swal2-popup-high-zindex",
        confirmButton: `swal2-confirm-${confirmButtonStyle} custom-confirm-button`,
        cancelButton: 'swal2-cancel'
      }
    };

    // Set icon and colors based on style
    switch (confirmButtonStyle) {
      case "success":
        swalConfig.icon = 'success';
        swalConfig.confirmButtonColor = '#28a745';
        break;
      case "error":
        swalConfig.icon = 'error';
        swalConfig.confirmButtonColor = 'rgb(242, 33, 18)';
        break;
      case "warning":
        swalConfig.icon = 'warning';
        swalConfig.confirmButtonColor = '#ffc107';
        break;
      case "info":
        swalConfig.icon = 'info';
        swalConfig.confirmButtonColor = '#17a2b8';
        break;
      default:
        swalConfig.icon = 'success';
        swalConfig.confirmButtonColor = '#28a745';
    }

    Swal.fire(swalConfig);
  };

  const handleGetNotificationCount = async () => {
    try {
      let response = await getCount();
      if (response?.data?.data?.count !== undefined) {
        setNotificationCount(response.data.data.count);
      }
    } catch (error) {
      console.error("Error getting notification count:", error);
      // Handle error gracefully - maybe set count to 0 or show a message
    }
  };

  const hideToastMessage = () => {
    Swal.close();
  };

  const handleChangeTimezone = (timezone) => {
    try {
      cookies.set("defaultTimezone", timezone, { path: "/" });
      setTimezone(timezone);
    } catch (error) {
      console.error("Error changing timezone:", error);
      // Still update the state even if cookie setting fails
      setTimezone(timezone);
    }
  };

  return (
    <AppContext.Provider
      value={{
        isLogin,
        alert,
        role,
        notificationCount,
        timezone,
        parentStoreTimezone,
        modules,
        isLoading,
        setIsLoading,
        handleGetNotificationCount,
        login,
        logout,
        handleChangeTimezone,
        changeModules,
        showToastMessage,
        lastOrderEditPath,
        setLastOrderEditPath,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

/* API FOR ROLES RETURN VALUE IN INT 1/0 SO IN UI WE NEED TO CONVERT TO BOOLEAN FOR CONDITION SO WE WILL CONVERT HERE BEFORE SETTING IN CONTEXT */
function convertPermissionsIntToBoolean(modules) {
  try {
    if (!Array.isArray(modules)) {
      console.warn("Modules is not an array:", modules);
      return [];
    }
    
    return modules.map((module) => {
      if (!module || !module.permissions) {
        console.warn("Invalid module structure:", module);
        return module;
      }
      
      return {
        ...module,
        permissions: {
          ...module.permissions,
          read_access: Boolean(module.permissions.read_access),
          write_access: Boolean(module.permissions.write_access),
          delete_access: Boolean(module.permissions.delete_access),
        },
      };
    });
  } catch (error) {
    console.error("Error converting permissions:", error);
    return [];
  }
}

const useAuth = () => useContext(AppContext);

export { AppProvider, useAuth };
